<script lang="ts">
    import { Constants } from '@/data/constants';
    import { characterNameTooltipChoices } from '@/data/settings';
    import { settingsState } from '@/shared/state/settings.svelte';
    import { timeStore } from '@/shared/stores/time';
    import { userStore } from '@/stores';
    import { toNiceDuration } from '@/utils/formatting';
    import { getCharacterRested } from '@/utils/get-character-rested';
    import getRaiderIoColor from '@/utils/get-raider-io-color';
    import type { Character } from '@/types';

    import CurrentLocation from '@/components/home/table/row/HomeTableRowCurrentLocation.svelte';
    import ItemLevel from '@/components/character-table/row/ItemLevel.svelte';
    import Keystone from '@/components/character-table/row/Keystone.svelte';
    import LastSeenAddon from '@/components/character-table/row/LastSeenAddon.svelte';

    let { character }: { character: Character } = $props();

    let enabledChoices = $derived.by(() =>
        characterNameTooltipChoices.filter(
            (choice) => !settingsState.value.characters.disabledNameTooltip.includes(choice.id)
        )
    );
</script>

<style lang="scss">
    td:first-child {
        --width: 6rem;

        border-right: 1px solid var(--border-color);
        text-align: right;
    }
    table {
        :global(td:last-child) {
            --max-width: 20rem;
            --width: 15rem;

            text-align: left !important;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
</style>

<div class="wowthing-tooltip">
    <h4>{character.name} - {character.realm.name}</h4>

    {#if character.guildId && !settingsState.value.characters.disabledNameTooltip.includes('guild')}
        <h5>
            {#if character.guild}
                &lt;{character.guild.name || 'Unknown Guild'}&gt;
                {character.guild.realm?.name || 'Unknown Realm'}
            {:else}
                &lt;Unknown Guild #{character.guildId}&gt;
            {/if}
        </h5>
    {/if}

    <table class="table-striped">
        <tbody>
            {#each enabledChoices as { id } (id)}
                {#if id === 'currentLocation'}
                    <tr>
                        <td>Current loc.</td>
                        <CurrentLocation {character} />
                    </tr>
                {:else if id === 'hearthLocation'}
                    <tr>
                        <td>Hearth loc.</td>
                        <td>{character.hearthLocation || '---'}</td>
                    </tr>
                {:else if id === 'itemLevel'}
                    <tr>
                        <td>Item level</td>
                        <ItemLevel {character} />
                    </tr>
                {:else if id === 'last'}
                    <tr>
                        <td>Addon seen</td>
                        <LastSeenAddon {character} />
                    </tr>
                    <tr>
                        <td>API update</td>
                        <td>
                            {#if character.lastApiUpdate}
                                {@const diff = $timeStore.diff(character.lastApiUpdate).toMillis()}
                                <code>{@html toNiceDuration(diff)}</code> ago
                            {:else}
                                ???
                            {/if}
                        </td>
                    </tr>
                {:else if id === 'mythicPlusKeystone'}
                    <tr>
                        <td>M+ key</td>
                        <Keystone {character} />
                    </tr>
                {:else if id === 'mythicPlusScore'}
                    {@const scores = character.raiderIo?.[Constants.mythicPlusSeason]}
                    {@const tiers = $userStore.raiderIoScoreTiers?.[Constants.mythicPlusSeason]}
                    {@const overallScore =
                        character.mythicPlusSeasonScores?.[Constants.mythicPlusSeason] ||
                        scores?.['all'] ||
                        0}
                    <tr>
                        <td>M+ score</td>
                        <td style:color={getRaiderIoColor(tiers, overallScore)}>
                            {overallScore.toFixed(1)}
                        </td>
                    </tr>
                {:else if id === 'playedTime'}
                    <tr>
                        <td>Played time</td>
                        <td>
                            <code
                                >{@html toNiceDuration(character.playedTotal * 1000).replace(
                                    '&nbsp;',
                                    ''
                                )}</code
                            >
                        </td>
                    </tr>
                {:else if id === 'restedXp' && character.level < Constants.characterMaxLevel}
                    {@const [rested, restedRemaining] = getCharacterRested($timeStore, character)}
                    <tr>
                        <td>Rested XP</td>
                        <td>
                            {rested}
                            {#if restedRemaining}
                                <span> | {@html restedRemaining}</span>
                            {/if}
                        </td>
                    </tr>
                {/if}
            {/each}
        </tbody>
    </table>
</div>
